import { ModuleType } from 'infrastructure/enums';
import { useTranslation } from 'react-i18next';
import { isMobile } from 'utils/deviceHelper';
import useUser from 'utils/useUser';
import { isPatient } from 'utils/userTypeHelper';

import type { IRpmActivityCountResponse } from 'infrastructure/interfaces/i-rpm-patient-activity';

interface ILink {
  key: string;
  title: string;
  to: string;
  dataCy: string;
  render: boolean;
  marked?: boolean;
  getColor?: (activityCount?: IRpmActivityCountResponse) => string;
  iconColor?: string;
}

export const usePatientChartNavSection = () => {
  const { userType, deviceType } = useUser();
  const { t } = useTranslation();

  const linkToBanner = isMobile(deviceType)
    ? '#patient-banner-mob'
    : '#patient-banner';

  const getIndicatorColor = (activityCount?: IRpmActivityCountResponse) => {
    if (!activityCount) return '';

    if (activityCount.critical > 0) return 'red';
    if (activityCount.risk > 0) return 'yellow';
    if (activityCount.normal > 0) return 'green';

    return '';
  };

  const generateLinksWithIndicators = (
    linksList: Record<ModuleType, ILink[]>,
    module: ModuleType,
    activityCount?: IRpmActivityCountResponse,
  ): ILink[] => {
    const links = linksList[module as keyof typeof linksList];

    if (!links) {
      return [];
    }

    if (!activityCount) return links;

    return links.map((link) => {
      if (link.getColor) {
        return {
          ...link,
          iconColor: link.getColor(activityCount),
        };
      }
      return link;
    });
  };

  const linksList: Record<ModuleType, ILink[]> = {
    [ModuleType.RPM]: [
      {
        key: 'average-metrics',
        title: t('labels.average'),
        to: '#average-metrics',
        dataCy: 'sidebar-average-metrics',
        render: true,
      },
      {
        key: 'graphs',
        title: t('labels.graphs'),
        to: '#graphs',
        dataCy: 'sidebar-graphs',
        render: true,
      },
      {
        key: 'activity',
        title: t('labels.activity'),
        to: '#activity',
        dataCy: 'sidebar-activity',
        render: true,
      },
      {
        key: 'medication',
        title: t('labels.medication'),
        to: '#medication',
        dataCy: 'sidebar-medication',
        render: true,
      },
      {
        key: 'clinical-activity',
        title: t('labels.activitiesLog'),
        to: '#clinical-activity',
        dataCy: 'sidebar-clinical-activity',
        render: !isPatient(userType),
      },
      {
        key: 'user-profile',
        title: t('labels.patientInfo'),
        to: '#user-profile',
        dataCy: 'sidebar-patient-info',
        render: true,
      },
      {
        key: 'general-comments',
        title: t('labels.generalComments'),
        to: '#general-comments',
        dataCy: 'sidebar-general-comment',
        render: !isPatient(userType),
      },
    ].filter(({ render }) => render),

    [ModuleType.CCM]: [
      {
        key: 'appointment-details',
        title: t('labels.appointment'),
        to: '#appointment-details',
        dataCy: 'sidebar-appointment-details',
        render: true,
      },
      {
        key: 'questionnaire',
        title: t('labels.questionnaire'),
        to: '#questionnaire',
        dataCy: 'sidebar-questionnaire',
        render: true,
      },
      {
        key: 'medical-history',
        title: t('labels.medicalHistory'),
        to: '#medical-history',
        dataCy: 'sidebar-medical-history',
        render: true,
      },
      {
        key: 'allergy-history',
        title: t('labels.allergyLog'),
        to: '#allergy-history',
        dataCy: 'sidebar-allergy-history',
        render: true,
      },
      {
        key: 'clinical-activity',
        title: t('labels.activitiesLog'),
        to: '#clinical-activity',
        dataCy: 'sidebar-clinical-activity',
        render: !isPatient(userType),
      },
      {
        key: 'medication',
        title: t('labels.medication'),
        to: '#medication',
        dataCy: 'sidebar-medication',
        render: true,
      },
      {
        key: 'patient-goals',
        title: t('labels.patientGoals'),
        to: '#patient-goals',
        dataCy: 'sidebar-patient-goals',
        render: true,
      },
      {
        key: 'user-profile',
        title: t('labels.patientInfo'),
        to: '#user-profile',
        dataCy: 'sidebar-patient-info',
        render: true,
      },
      {
        key: 'general-comments',
        title: t('labels.generalComments'),
        to: '#general-comments',
        dataCy: 'sidebar-general-comment',
        render: !isPatient(userType),
      },
    ].filter(({ render }) => render),

    [ModuleType.ADMIN]: [
      {
        key: 'patient-banner',
        title: t('labels.patientBanner'),
        to: linkToBanner,
        dataCy: 'sidebar-patient-banner',
        render: true,
      },
      {
        key: 'user-profile',
        title: t('labels.patientInfo'),
        to: '#user-profile',
        dataCy: 'sidebar-patient-info',
        render: true,
      },
      {
        key: 'general-comments',
        title: t('labels.generalComments'),
        to: '#general-comments',
        dataCy: 'sidebar-general-comment',
        render: true,
      },
    ].filter(({ render }) => render),

    [ModuleType.RPMCCM]: [
      {
        key: 'patient-snapshot',
        title: t('patientSnapshot.patientSnapshot'),
        to: '#patient-snapshot',
        dataCy: 'sidebar-patient-snapshot',
        render: true,
      },
      {
        key: 'combine-activity',
        title: t('labels.activity'),
        to: '#combine-activity',
        getColor: getIndicatorColor,
        dataCy: 'sidebar-combine-activity',
        render: true,
        marked: true,
      },
      {
        key: 'average-metrics',
        title: t('labels.averageAndTrends'),
        to: '#average-metrics',
        dataCy: 'sidebar-average-metrics',
        render: true,
      },
      {
        key: 'medical-history',
        title: t('labels.patientHistory'),
        to: '#medical-history',
        dataCy: 'sidebar-medical-history',
        render: true,
      },
      {
        key: 'clinical-activity',
        title: t('labels.activitiesLog'),
        to: '#clinical-activity',
        dataCy: 'sidebar-clinical-activity',
        render: true,
      },
      {
        key: 'medication',
        title: t('labels.medication'),
        to: '#medication',
        dataCy: 'sidebar-medication',
        render: true,
      },
      {
        key: 'services',
        title: t('labels.ccm'),
        to: '#services',
        dataCy: 'sidebar-services',
        render: true,
      },
      {
        key: 'user-profile',
        title: t('labels.patientInfo'),
        to: '#user-profile',
        dataCy: 'sidebar-patient-info',
        render: true,
      },
      {
        key: 'general-comments',
        title: t('labels.generalComments'),
        to: '#general-comments',
        dataCy: 'sidebar-general-comment',
        render: !isPatient(userType),
      },
    ],
    [ModuleType.APCM]: [],
  } as const;

  return { linksList, generateLinksWithIndicators };
};
